import { IOption } from 'components/_form/Select/Select';
import { IUser } from 'types/user';
import { getUserDisplayLabel } from 'utilities/fullName';

export interface IUserSelectOption extends IOption {
  first_name?: string | null;
  last_name?: string | null;
  email?: string | null;
  username?: string | null;
  photo?: string | null;
  profile?: {
    first_name?: string;
    last_name?: string;
    photo?: string;
  } | null;
}

export type UserSelectOptionSource = {
  id: string | number;
  first_name?: string | null;
  last_name?: string | null;
  email?: string | null;
  username?: string | null;
  photo?: string | null;
  profile?: IUserSelectOption['profile'];
};

export const toUserSelectOption = (
  member: UserSelectOptionSource
): IUserSelectOption => {
  const first_name = member.first_name ?? member.profile?.first_name;
  const last_name = member.last_name ?? member.profile?.last_name;

  return {
    value: String(member.id),
    label: getUserDisplayLabel({
      first_name,
      last_name,
      email: member.email,
      username: member.username
    }),
    first_name,
    last_name,
    email: member.email,
    username: member.username,
    photo: member.photo ?? member.profile?.photo
  };
};

export const mapUsersToSelectOptions = (users: IUser[]): IUserSelectOption[] =>
  users.map((user) =>
    toUserSelectOption({
      id: user.id,
      email: user.email,
      username: user.username,
      profile: user.profile
    })
  );

/**
 * usersList firmy + osoby spoza listy (lustro / author z API).
 * Jak Hash.merge w Ruby: istniejące id zostają, brakujące doklejamy.
 */
export const mergeUserSelectOptions = (
  usersList: IUserSelectOption[],
  fallbacks: Array<UserSelectOptionSource | IUserSelectOption | null | undefined>
): IUserSelectOption[] => {
  const byId = new Map(
    usersList.map((user) => [user.value.toString(), user] as const)
  );

  fallbacks.forEach((fallback) => {
    if (!fallback) return;

    const option =
      'value' in fallback && 'label' in fallback
        ? (fallback as IUserSelectOption)
        : toUserSelectOption(fallback as UserSelectOptionSource);

    const id = option.value?.toString();
    if (id && !byId.has(id)) {
      byId.set(id, option);
    }
  });

  return Array.from(byId.values());
};

export const resolveUserSelectOption = (
  options: IUserSelectOption[],
  selected?: IUserSelectOption | IUserSelectOption[]
): IUserSelectOption | IUserSelectOption[] | undefined => {
  if (selected == null) return selected;

  if (Array.isArray(selected)) {
    return selected.map(
      (item) => options.find((o) => o.value === String(item.value)) ?? item
    );
  }

  return options.find((o) => o.value === String(selected.value)) ?? selected;
};
